import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, ChevronDown, Link, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import client from '@/api/client';
import { uploadApi, classifyDesignFile } from '@/api/uploadApi';
import { VariantSupplyEditor, isPersistedVariantId } from './VariantSupplyEditor';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import { EditSupplyModal } from '@/components/supplies/EditSupplyModal';
import type { ProductionMethod, Supply, SupplyType } from '@/types/api';
import type { ProductFormState, VariantFilamentFormRow, VariantSupplyFormRow } from './types';

interface StepMaterialsProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
  isEditMode?: boolean;
}

function newFilamentRow(sortOrder: number): VariantFilamentFormRow {
  return {
    id: `vf-${Date.now()}-${sortOrder}`,
    supplyId: '',
    grams: '50',
    extruderSlot: String(sortOrder + 1),
    sortOrder,
    notes: '',
  };
}

function newVariantSupplyRow(): VariantSupplyFormRow {
  return {
    id: `vs-${Date.now()}`,
    supplyId: '',
    quantityRequired: '1',
    unitOfMeasure: 'piece',
    notes: '',
  };
}

export function StepMaterials({ state, updateState, isEditMode = false }: StepMaterialsProps) {
  const queryClient = useQueryClient();
  const [showAddSupply, setShowAddSupply] = useState(false);
  const [supplyPickerId, setSupplyPickerId] = useState('');
  const [supplyQty, setSupplyQty] = useState('1');
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const designInputRef = useRef<HTMLInputElement>(null);

  const { data: methods } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
  });

  const { data: allSupplies } = useQuery({
    queryKey: ['supplies', 'all-active'],
    queryFn: () =>
      client.get<Supply[]>('/supplies/all').then((response) => response.data),
  });

  const allowedSupplyTypes = useMemo(() => {
    const selected = (methods ?? []).filter((m: ProductionMethod) =>
      state.productionMethodIds.includes(m.id)
    );
    const types = new Set<SupplyType>();
    selected.forEach((m) => m.supplyTypes?.forEach((t) => types.add(t)));
    return types;
  }, [methods, state.productionMethodIds]);

  const compatibilityFilteredSupplies = useMemo(() => {
    if (!allSupplies) return [];
    return allSupplies.filter((s) => {
      if (!s.isActive) return false;
      if (state.itemTypeId && s.compatibleItemTypeIds?.length) {
        if (!s.compatibleItemTypeIds.includes(state.itemTypeId)) return false;
      }
      if (state.productionMethodIds.length && s.productionMethodIds?.length) {
        if (!state.productionMethodIds.some((id) => s.productionMethodIds!.includes(id))) return false;
      }
      return true;
    });
  }, [allSupplies, state.itemTypeId, state.productionMethodIds]);

  const filteredSupplies = useMemo(() => {
    if (allowedSupplyTypes.size === 0) {
      return compatibilityFilteredSupplies;
    }
    return compatibilityFilteredSupplies.filter((s) => allowedSupplyTypes.has(s.supplyType));
  }, [compatibilityFilteredSupplies, allowedSupplyTypes]);

  const suggestedSupplies = useMemo(() => {
    return filteredSupplies
      .filter((s) => {
        const itemMatch = Boolean(
          state.itemTypeId && s.compatibleItemTypeIds?.includes(state.itemTypeId)
        );
        const methodMatch = state.productionMethodIds.some((id) =>
          s.productionMethodIds?.includes(id)
        );
        return itemMatch || methodMatch;
      })
      .filter((s) => !state.supplies.some((ps) => ps.supplyId === s.id))
      .slice(0, 8);
  }, [filteredSupplies, state.itemTypeId, state.productionMethodIds, state.supplies]);

  const hardwareSupplies = filteredSupplies.filter((s) =>
    ['HARDWARE_COMPONENT', 'ACCESSORY', 'ELECTRONIC'].includes(s.supplyClass ?? '')
  );

  const blankSupplies = useMemo(() => {
    if (!allSupplies) return [];
    return allSupplies.filter(
      (s) =>
        s.isActive &&
        (s.isBlank ||
          s.supplyClass === 'CUSTOMIZABLE_BLANK' ||
          s.supplyClass === 'BLANK_APPAREL')
    );
  }, [allSupplies]);

  const handleBlankChange = (supplyId: string) => {
    const blank = blankSupplies.find((s) => s.id === supplyId);
    updateState((prev) => ({
      ...prev,
      blankSupplyId: supplyId,
      dimensionHeight:
        blank?.dimensionHeight != null ? String(blank.dimensionHeight) : prev.dimensionHeight,
      dimensionWidth:
        blank?.dimensionWidth != null ? String(blank.dimensionWidth) : prev.dimensionWidth,
      dimensionDepth:
        blank?.dimensionDepth != null ? String(blank.dimensionDepth) : prev.dimensionDepth,
    }));
  };

  const handleDesignUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDesign(true);
    try {
      const response = await uploadApi.uploadProductDesignFile(file);
      const kind = classifyDesignFile(file.name);
      updateState((prev) => {
        if (kind === 'model') {
          return {
            ...prev,
            modelFilePath: response.fileUrl,
            designFileName: response.originalFilename,
            designFileType: file.name.split('.').pop()?.toLowerCase() || 'stl',
          };
        }
        return {
          ...prev,
          designFilePath: response.fileUrl,
          designFileName: response.originalFilename,
          designFileType: file.name.split('.').pop()?.toLowerCase() || 'design',
        };
      });
    } finally {
      setUploadingDesign(false);
      if (designInputRef.current) designInputRef.current.value = '';
    }
  };

  const updateVariantSupplies = (
    variantId: string,
    supplies: VariantSupplyFormRow[]
  ) => {
    updateState((prev) => ({
      ...prev,
      variants: prev.variants.map((v) =>
        v.id === variantId ? { ...v, variantSupplies: supplies } : v
      ),
    }));
  };

  const addSuggestedSupply = (supply: Supply) => {
    updateState((prev) => ({
      ...prev,
      supplies: [
        ...prev.supplies,
        {
          id: `ps-${Date.now()}`,
          supplyId: supply.id,
          supplyName: supply.name,
          quantityRequired: '1',
          unitOfMeasure: supply.unitOfMeasure || 'piece',
          notes: '',
        },
      ],
    }));
  };

  const addProductSupply = () => {
    const supply = filteredSupplies.find((s) => s.id === supplyPickerId);
    if (!supply) return;
    updateState((prev) => ({
      ...prev,
      supplies: [
        ...prev.supplies,
        {
          id: `ps-${Date.now()}`,
          supplyId: supply.id,
          supplyName: supply.name,
          quantityRequired: supplyQty,
          unitOfMeasure: supply.unitOfMeasure || 'units',
          notes: '',
        },
      ],
    }));
    setSupplyPickerId('');
    setSupplyQty('1');
  };

  const removeProductSupply = (id: string) => {
    updateState((prev) => ({
      ...prev,
      supplies: prev.supplies.filter((ps) => ps.id !== id),
    }));
  };

  const updateVariantFilaments = (
    variantId: string,
    filaments: VariantFilamentFormRow[]
  ) => {
    updateState((prev) => ({
      ...prev,
      variants: prev.variants.map((v) =>
        v.id === variantId ? { ...v, variantFilaments: filaments } : v
      ),
    }));
  };

  const filamentSupplies = filteredSupplies.filter((s) => s.supplyType === 'FILAMENT');

  const handleSupplyCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['supplies'] });
    setShowAddSupply(false);
  };

  return (
    <div className="space-y-6">
      {blankSupplies.length > 0 && (
        <div className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl space-y-3">
          <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream">
            Blank Substrate <span className="text-xs font-normal text-gray-500">(optional)</span>
          </label>
          <p className="text-xs text-gray-600 dark:text-warm-sand/70">
            For apparel, UV, sublimation, or DTF — pick the blank from inventory. Dimensions auto-fill from the supply.
          </p>
          <select
            value={state.blankSupplyId}
            onChange={(e) => handleBlankChange(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
          >
            <option value="">— No blank selected —</option>
            {blankSupplies.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.dimensionWidth != null && s.dimensionHeight != null
                  ? ` (${s.dimensionWidth}×${s.dimensionHeight}${s.dimensionUnit || 'in'})`
                  : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="border border-gray-200 dark:border-charcoal-700 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-white dark:bg-charcoal-800/50 border-b border-gray-200 dark:border-charcoal-700">
          <span className="text-sm font-medium text-gray-900 dark:text-warm-cream flex items-center gap-2">
            <Boxes className="w-4 h-4 text-amber-450" />
            Bill of Materials
          </span>
        </div>
        <div className="p-4 space-y-3 bg-gray-50/50 dark:bg-charcoal-800/30">
          {suggestedSupplies.length > 0 && (
            <div className="p-3 bg-amber-450/5 border border-amber-450/20 rounded-lg space-y-2">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                Suggested for this product type & production methods
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestedSupplies.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => addSuggestedSupply(s)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white dark:bg-charcoal-800 border border-amber-450/30 hover:border-amber-450"
                  >
                    <Plus className="w-3 h-3" />
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {state.supplies.map((ps) => (
            <div
              key={ps.id}
              className="flex items-center gap-3 p-3 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-warm-cream truncate">
                  {ps.supplyName ||
                    allSupplies?.find((s) => s.id === ps.supplyId)?.name ||
                    'Supply'}
                </p>
                <p className="text-xs text-gray-600 dark:text-warm-sand">
                  Qty: {ps.quantityRequired} {ps.unitOfMeasure}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeProductSupply(ps.id)}
                className="p-2 text-danger-400 hover:bg-danger-500/10 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">
                Add supply
              </label>
              <div className="relative">
                <select
                  value={supplyPickerId}
                  onChange={(e) => setSupplyPickerId(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm appearance-none"
                >
                  <option value="">— Pick a supply —</option>
                  {filteredSupplies
                    .filter(
                      (s) => !state.supplies.some((ps) => ps.supplyId === s.id)
                    )
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.supplyType.replace(/_/g, ' ')})
                      </option>
                    ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-warm-taupe" />
              </div>
            </div>
            <div className="w-24">
              <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">Qty</label>
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={supplyQty}
                onChange={(e) => setSupplyQty(e.target.value)}
                className="w-full px-2 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
              />
            </div>
            <button
              type="button"
              onClick={addProductSupply}
              disabled={!supplyPickerId}
              className="btn-primary text-sm py-2"
            >
              <Plus className="w-4 h-4 mr-1 inline" />
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowAddSupply(true)}
              className="btn-secondary text-sm py-2"
            >
              Quick Add Supply
            </button>
          </div>
        </div>
      </div>

      {filamentSupplies.length > 0 && (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-cream mb-1">
              Variant Filament BOM
            </h4>
            <p className="text-xs text-gray-600 dark:text-warm-sand/70">
              Configure grams and extruder slot per color for each variant.
            </p>
          </div>

          {state.variants.map((variant) => (
            <div
              key={variant.id}
              className="p-4 border border-gray-200 dark:border-charcoal-700 rounded-xl space-y-3"
            >
              <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">
                {variant.name || 'Unnamed variant'}
              </p>

              {variant.variantFilaments.map((vf, idx) => (
                <div key={vf.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                  <div className="sm:col-span-5">
                    <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">
                      Filament
                    </label>
                    <select
                      value={vf.supplyId}
                      onChange={(e) => {
                        const next = [...variant.variantFilaments];
                        next[idx] = { ...vf, supplyId: e.target.value };
                        updateVariantFilaments(variant.id, next);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
                    >
                      <option value="">Select filament...</option>
                      {filamentSupplies.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">
                      Grams
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={vf.grams}
                      onChange={(e) => {
                        const next = [...variant.variantFilaments];
                        next[idx] = { ...vf, grams: e.target.value };
                        updateVariantFilaments(variant.id, next);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">
                      Extruder
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="8"
                      value={vf.extruderSlot}
                      onChange={(e) => {
                        const next = [...variant.variantFilaments];
                        next[idx] = { ...vf, extruderSlot: e.target.value };
                        updateVariantFilaments(variant.id, next);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() =>
                        updateVariantFilaments(
                          variant.id,
                          variant.variantFilaments.filter((row) => row.id !== vf.id)
                        )
                      }
                      className="p-2 text-danger-400 hover:bg-danger-500/10 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  updateVariantFilaments(variant.id, [
                    ...variant.variantFilaments,
                    newFilamentRow(variant.variantFilaments.length),
                  ])
                }
                className="text-sm text-amber-450 hover:text-amber-500 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add filament row
              </button>
            </div>
          ))}
        </div>
      )}

      {hardwareSupplies.length > 0 && !isEditMode && (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-cream mb-1">
              Variant Hardware BOM
            </h4>
            <p className="text-xs text-gray-600 dark:text-warm-sand/70">
              Optional per-variant components such as keychain rings or stand bases.
            </p>
          </div>

          {state.variants.map((variant) => (
            <div
              key={`hw-${variant.id}`}
              className="p-4 border border-gray-200 dark:border-charcoal-700 rounded-xl space-y-3"
            >
              <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">
                {variant.name || 'Unnamed variant'}
              </p>

              {variant.variantSupplies.map((vs, idx) => (
                <div key={vs.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                  <div className="sm:col-span-6">
                    <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">Component</label>
                    <select
                      value={vs.supplyId}
                      onChange={(e) => {
                        const next = [...variant.variantSupplies];
                        next[idx] = { ...vs, supplyId: e.target.value };
                        updateVariantSupplies(variant.id, next);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
                    >
                      <option value="">Select component...</option>
                      {hardwareSupplies.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">Qty</label>
                    <input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={vs.quantityRequired}
                      onChange={(e) => {
                        const next = [...variant.variantSupplies];
                        next[idx] = { ...vs, quantityRequired: e.target.value };
                        updateVariantSupplies(variant.id, next);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() =>
                        updateVariantSupplies(
                          variant.id,
                          variant.variantSupplies.filter((row) => row.id !== vs.id)
                        )
                      }
                      className="p-2 text-danger-400 hover:bg-danger-500/10 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  updateVariantSupplies(variant.id, [
                    ...variant.variantSupplies,
                    newVariantSupplyRow(),
                  ])
                }
                className="text-sm text-amber-450 hover:text-amber-500 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add hardware row
              </button>
            </div>
          ))}
        </div>
      )}

      {isEditMode && hardwareSupplies.length > 0 && (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-warm-cream mb-1">
              Variant Hardware BOM
            </h4>
            <p className="text-xs text-gray-600 dark:text-warm-sand/70">
              Manage saved components per variant (keychain rings, stand bases, etc.).
            </p>
          </div>
          {state.variants
            .filter((v) => isPersistedVariantId(v.id))
            .map((variant) => (
              <VariantSupplyEditor
                key={`edit-hw-${variant.id}`}
                variantId={variant.id}
                variantName={variant.name}
                hardwareSupplies={hardwareSupplies}
              />
            ))}
        </div>
      )}

      <div className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl space-y-3">
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream">
          Design File
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            ref={designInputRef}
            type="file"
            accept=".stl,.3mf,.svg,.dst,.pdf"
            onChange={handleDesignUpload}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => designInputRef.current?.click()}
            disabled={uploadingDesign}
            className="btn-secondary text-sm py-2 inline-flex items-center gap-2"
          >
            {uploadingDesign ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Upload STL / design file
          </button>
        </div>
        {(state.designFilePath || state.modelFilePath) && (
          <div className="text-xs text-gray-600 dark:text-warm-sand space-y-1">
            {state.modelFilePath && (
              <p>
                3D model:{' '}
                <a href={state.modelFilePath} target="_blank" rel="noreferrer" className="text-amber-450 hover:underline">
                  {state.designFileName || 'Uploaded model'}
                </a>
              </p>
            )}
            {state.designFilePath && (
              <p>
                Design file:{' '}
                <a href={state.designFilePath} target="_blank" rel="noreferrer" className="text-amber-450 hover:underline">
                  {state.designFileName || 'Uploaded design'}
                </a>
              </p>
            )}
          </div>
        )}
        <div className="relative">
          <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
          <input
            type="url"
            value={state.designExternalUrl}
            onChange={(e) =>
              updateState((prev) => ({ ...prev, designExternalUrl: e.target.value }))
            }
            placeholder="https://drive.google.com/... or uploaded file URL"
            className="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
          />
        </div>
      </div>

      <EditSupplyModal
        isOpen={showAddSupply}
        onClose={() => setShowAddSupply(false)}
        onSuccess={handleSupplyCreated}
      />
    </div>
  );
}
